import { ZONE_KINDS, type Camera } from '@playplate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { CameraStatusPill, EmptyState, ErrorState, Loading, PageHeader, Panel } from '../components/ui';
import { useAuth } from '../hooks/useAuth';
import { api, ApiError } from '../lib/api';
import { formatRelative } from '../lib/format';

type CameraForm = {
  name: string;
  location: string;
  zoneKind: string;
  host: string;
  port: number;
  pathMain: string;
  pathSub: string;
  username: string;
  password: string;
  inferenceFps: number;
  inferenceImgsz: number;
  confidenceThreshold: number;
  motionGate: boolean;
  recordingByNvr: boolean;
  enabled: boolean;
};

const EMPTY_FORM: CameraForm = {
  name: '',
  location: '',
  zoneKind: 'DINING',
  host: '',
  port: 554,
  // Hikvision/Dahua-style defaults: channel 101 is the main stream, 102 the sub.
  // AI runs on the sub-stream so the NVR keeps the main one for recording.
  pathMain: '/Streaming/Channels/101',
  pathSub: '/Streaming/Channels/102',
  username: '',
  password: '',
  inferenceFps: 4,
  inferenceImgsz: 640,
  confidenceThreshold: 0.4,
  motionGate: true,
  recordingByNvr: true,
  enabled: true,
};

export function CamerasPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Camera | null>(null);
  const [creating, setCreating] = useState(false);

  const camerasQuery = useQuery({
    queryKey: ['cameras', 'list'],
    queryFn: () => api.get<{ items: Camera[] }>('/cameras'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/cameras/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['cameras'] }),
  });

  if (camerasQuery.isLoading) return <Loading label="Loading cameras" />;
  if (camerasQuery.isError) {
    return <ErrorState message={(camerasQuery.error as Error).message} onRetry={() => void camerasQuery.refetch()} />;
  }

  const cameras = camerasQuery.data!.items;
  const canWrite = can('camera:write');

  return (
    <div>
      <PageHeader
        title="Cameras"
        subtitle={`${cameras.length} configured · AI runs on the sub-stream, the NVR keeps recording the main stream`}
        actions={
          canWrite ? (
            <button type="button" className="btn-primary" onClick={() => { setCreating(true); setEditing(null); }}>
              <Plus className="h-4 w-4" aria-hidden />
              Add camera
            </button>
          ) : null
        }
      />

      {creating || editing ? (
        <CameraFormPanel
          camera={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      ) : null}

      {cameras.length === 0 ? (
        <EmptyState
          title="No cameras yet"
          message="Add an RTSP camera from the store network. Credentials are encrypted and never sent to the browser."
        />
      ) : (
        <Panel className="mt-4">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[840px]">
              <thead>
                <tr>
                  <th className="table-header">Camera</th>
                  <th className="table-header">Zone</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Last frame</th>
                  <th className="table-header">Inference</th>
                  <th className="table-header">Host</th>
                  {canWrite ? <th className="table-header w-px" /> : null}
                </tr>
              </thead>
              <tbody>
                {cameras.map((camera) => (
                  <tr key={camera.id} className="transition-colors hover:bg-panel-raised/60">
                    <td className="table-cell">
                      <button
                        type="button"
                        className="text-left font-semibold hover:text-lime disabled:cursor-default disabled:hover:text-paper"
                        disabled={!canWrite}
                        onClick={() => { setEditing(camera); setCreating(false); }}
                      >
                        {camera.name}
                      </button>
                      <p className="text-xs text-muted">{camera.location}</p>
                    </td>
                    <td className="table-cell text-muted">{camera.zoneKind.replace('_', '/')}</td>
                    <td className="table-cell">
                      <CameraStatusPill status={camera.status} fps={camera.currentFps} />
                      {camera.statusReason ? (
                        <p className="mt-1 max-w-[220px] truncate text-xs text-muted" title={camera.statusReason}>
                          {camera.statusReason}
                        </p>
                      ) : null}
                    </td>
                    <td className="table-cell font-mono text-xs text-muted">
                      {formatRelative(camera.lastFrameAt)}
                    </td>
                    <td className="table-cell font-mono text-xs text-muted">
                      {camera.inferenceFps} fps · {camera.inferenceImgsz}px
                      {camera.motionGate ? ' · gated' : ''}
                    </td>
                    <td className="table-cell font-mono text-xs text-muted">
                      {camera.host}
                      {camera.hasCredentials ? (
                        <span className="ml-2 rounded bg-online/10 px-1.5 py-0.5 text-[10px] text-online">
                          credentials set
                        </span>
                      ) : (
                        <span className="ml-2 rounded bg-high/10 px-1.5 py-0.5 text-[10px] text-high">
                          no credentials
                        </span>
                      )}
                    </td>
                    {canWrite ? (
                      <td className="table-cell">
                        <button
                          type="button"
                          className="btn-ghost p-2 text-critical hover:bg-critical/10"
                          aria-label={`Remove ${camera.name}`}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Remove "${camera.name}"? Its history is kept, but it will stop being monitored.`,
                              )
                            ) {
                              remove.mutate(camera.id);
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </div>
  );
}

function CameraFormPanel({ camera, onClose }: { camera: Camera | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<CameraForm>(() =>
    camera
      ? {
          ...EMPTY_FORM,
          name: camera.name,
          location: camera.location,
          zoneKind: camera.zoneKind,
          host: camera.host,
          inferenceFps: camera.inferenceFps,
          inferenceImgsz: camera.inferenceImgsz,
          confidenceThreshold: camera.confidenceThreshold,
          motionGate: camera.motionGate,
          recordingByNvr: camera.recordingByNvr,
          enabled: camera.enabled,
        }
      : EMPTY_FORM,
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (payload: Partial<CameraForm>) =>
      camera ? api.patch(`/cameras/${camera.id}`, payload) : api.post('/cameras', payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cameras'] });
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not save the camera'),
  });

  const update = <K extends keyof CameraForm>(key: K, value: CameraForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const payload: Partial<CameraForm> = { ...form };
    // An empty password field on an edit means "leave the stored one alone" —
    // there is no way to accidentally blank a working credential.
    if (camera && !form.password) delete payload.password;
    save.mutate(payload);
  };

  return (
    <Panel title={camera ? `Edit ${camera.name}` : 'Add camera'} className="mb-4">
      <form onSubmit={handleSubmit} className="grid gap-4 p-4 md:grid-cols-2">
        <Field label="Name">
          <input className="input" required value={form.name} onChange={(e) => update('name', e.target.value)} />
        </Field>
        <Field label="Location">
          <input
            className="input"
            required
            value={form.location}
            onChange={(e) => update('location', e.target.value)}
          />
        </Field>

        <Field label="Zone kind">
          <select className="input" value={form.zoneKind} onChange={(e) => update('zoneKind', e.target.value)}>
            {ZONE_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {kind.replace('_', '/')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Host (private network only)" hint="e.g. 192.168.1.64 — cameras must never be internet-facing">
          <input
            className="input font-mono"
            required
            value={form.host}
            onChange={(e) => update('host', e.target.value)}
          />
        </Field>

        <Field label="RTSP port">
          <input
            type="number"
            className="input font-mono"
            value={form.port}
            onChange={(e) => update('port', Number(e.target.value))}
          />
        </Field>
        <Field label="Username">
          <input
            className="input font-mono"
            autoComplete="off"
            value={form.username}
            onChange={(e) => update('username', e.target.value)}
          />
        </Field>

        <Field label="Main stream path" hint="Used for live view and NVR recording">
          <input
            className="input font-mono"
            value={form.pathMain}
            onChange={(e) => update('pathMain', e.target.value)}
          />
        </Field>
        <Field label="Sub stream path" hint="Low-resolution stream used for AI inference">
          <input
            className="input font-mono"
            value={form.pathSub}
            onChange={(e) => update('pathSub', e.target.value)}
          />
        </Field>

        <Field
          label="Password"
          hint={camera ? 'Leave blank to keep the stored password' : 'Encrypted at rest, never returned by the API'}
        >
          <input
            type="password"
            className="input font-mono"
            autoComplete="new-password"
            value={form.password}
            onChange={(e) => update('password', e.target.value)}
          />
        </Field>

        <Field label="Inference rate" hint="Frames per second sent to the detector. 3–5 is plenty for counting.">
          <input
            type="number"
            step="0.5"
            min="0.5"
            max="15"
            className="input font-mono"
            value={form.inferenceFps}
            onChange={(e) => update('inferenceFps', Number(e.target.value))}
          />
        </Field>

        <Field label="Inference resolution" hint="Larger detects smaller people, at proportionally more CPU/GPU">
          <select
            className="input"
            value={form.inferenceImgsz}
            onChange={(e) => update('inferenceImgsz', Number(e.target.value))}
          >
            {[416, 512, 640, 960, 1280].map((size) => (
              <option key={size} value={size}>
                {size}px
              </option>
            ))}
          </select>
        </Field>

        <Field label="Confidence threshold" hint="Lower catches more people and more false positives">
          <input
            type="number"
            step="0.05"
            min="0.05"
            max="0.95"
            className="input font-mono"
            value={form.confidenceThreshold}
            onChange={(e) => update('confidenceThreshold', Number(e.target.value))}
          />
        </Field>

        <div className="flex flex-wrap items-center gap-6 md:col-span-2">
          <Checkbox
            label="Motion gating"
            hint="Skip inference on a static scene"
            checked={form.motionGate}
            onChange={(v) => update('motionGate', v)}
          />
          <Checkbox
            label="NVR records this camera"
            checked={form.recordingByNvr}
            onChange={(v) => update('recordingByNvr', v)}
          />
          <Checkbox label="Enabled" checked={form.enabled} onChange={(v) => update('enabled', v)} />
        </div>

        {error ? (
          <p className="rounded-lg border border-critical/40 bg-critical/10 px-3 py-2 text-sm text-critical md:col-span-2">
            {error}
          </p>
        ) : null}

        <div className="flex gap-2 md:col-span-2">
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : camera ? 'Save changes' : 'Add camera'}
          </button>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Panel>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="label mb-1.5 block">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-xs text-muted">{hint}</span> : null}
    </label>
  );
}

function Checkbox({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-2 text-sm">
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 rounded border-line bg-ink accent-lime"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        {label}
        {hint ? <span className="block text-xs text-muted">{hint}</span> : null}
      </span>
    </label>
  );
}
